<template>
  <div class="mb-4 flex flex-col gap-2">
    <CTimelineModal
      v-if="showTimeline && changelogData?.length"
      class=""
      :data="changelogData"
      @close="showTimeline = false"
      @item-activated="(item: ExtendedTimelineItemProps) => applyHistoryItem(item.id)"
    />
    <NButtonGroup vertical>
      <CButton
        v-tooltip="t('canvasUI.canvasBuilder.components.history.undo')"
        class="h-11 w-11"
        :disabled="activeHistoryItemIndex === 0"
        @click="undoCanvasHistoryItem"
      >
        <template #icon>
          <CIcon icon="rotate-left" size="18" />
        </template>
      </CButton>
      <CButton
        v-tooltip="t('canvasUI.canvasBuilder.components.history.redo')"
        class="h-11 w-11"
        :disabled="activeHistoryItemIndex === canvasHistory.length - 1"
        @click="redoCanvasHistoryItem"
      >
        <template #icon>
          <CIcon icon="rotate-right" size="18" />
        </template>
      </CButton>
    </NButtonGroup>

    <CButton
      v-tooltip="t('canvasUI.canvasBuilder.components.history.log')"
      class="h-11 w-11"
      size="large"
      @click="showTimeline = !showTimeline"
    >
      <template #icon>
        <CIcon icon="clock" size="18" />
      </template>
    </CButton>
  </div>
</template>
<script setup lang="ts">
import { computed, type ComputedRef, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { NButtonGroup } from 'naive-ui'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CTimelineModal from '@lib/components/CTimeline/CTimelineModal.vue'
import tooltipDirective from '@lib/directives/tooltip'
import useCanvasHistory from '@canvas-builder/composables/useCanvasHistory'
import { getComponentTitle } from '@canvas-builder/util/canvas.util'
import { ComponentTypes, CanvasHistoryAction } from '@canvas-builder/types/canvas'
import type { ExtendedTimelineItemProps } from '@lib/components/CTimeline/CTimeline.vue'
import { formatDateTime } from '@lib/util/date'

const { t } = useI18n()
const { activeHistoryItemIndex, applyHistoryItem, canvasHistory, undoCanvasHistoryItem, redoCanvasHistoryItem } =
  useCanvasHistory()

const vTooltip = tooltipDirective

const showTimeline = ref(false)

const getHistoryItemTitle = (type: ComponentTypes, action: CanvasHistoryAction) => {
  let translatedAction = ''
  switch (action) {
    case CanvasHistoryAction.ADD:
      translatedAction = t('canvasUI.canvasBuilder.components.history.actions.added')
      break
    case CanvasHistoryAction.OPEN:
      translatedAction = t('canvasUI.canvasBuilder.components.history.actions.opened')
      break
    case CanvasHistoryAction.REORDER:
      translatedAction = t('canvasUI.canvasBuilder.components.history.actions.reordered')
      break
    case CanvasHistoryAction.REMOVE:
      translatedAction = t('canvasUI.canvasBuilder.components.history.actions.removed')
      break
    case CanvasHistoryAction.SAVE:
      translatedAction = t('canvasUI.canvasBuilder.components.history.actions.saved')
      break
    default:
      break
  }
  if (type === ComponentTypes.Root) return `${getComponentTitle(type, t)} ${translatedAction}`

  return `${t('canvasUI.canvasBuilder.components.history.itemTitle', {
    componentType: getComponentTitle(type, t),
  })} ${translatedAction}`
}

const changelogData: ComputedRef<ExtendedTimelineItemProps[]> = computed(() =>
  canvasHistory.value
    .map((item) => ({
      active: item.active,
      time: formatDateTime(item.timestamp) as string,
      icon: item.icon,
      id: item.id,
      title: getHistoryItemTitle(item.componentType, item.action),
    }))
    .reverse(),
)
</script>
